import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { getImageUrl } from '@/utils/helpers';
import Header from '@/components/layout/Header';
import ArtworkModal from '@/components/artwork/ArtworkModal';
import { 
  Edit, Plus, Heart, MessageCircle, UserPlus, UserCheck, 
  Grid3X3, Bookmark, Settings, Loader2, User, Lock, Eye, EyeOff, Palette, Calendar,
  ShoppingBag, TrendingUp, Truck, Package, CheckCircle, Star, Clock, Edit3, Trash2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const UserProfilePage = () => {
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, token } = useAuth();
  
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [savedPosts, setSavedPosts] = useState([]);
  const [artworks, setArtworks] = useState([]);  // 用户发布的作品
  const [savedArtworks, setSavedArtworks] = useState([]); // 用户收藏的作品
  const [customizeOrders, setCustomizeOrders] = useState([]); // 定制订单
  const [investments, setInvestments] = useState([]); // 投资记录
  const [exhibitions, setExhibitions] = useState([]); // 用户创建的展览
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'posts');
  const [selectedPost, setSelectedPost] = useState(null);
  
  // Dialogs
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showCreateArtwork, setShowCreateArtwork] = useState(false);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [editingPostId, setEditingPostId] = useState(null);
  const [editingArtworkId, setEditingArtworkId] = useState(null);
  
  // Forms
  const [editForm, setEditForm] = useState({ name: '', bio: '', avatar: '', user_title: '', user_type: 'artist' });
  const [newPost, setNewPost] = useState({ content: '', images: [], tags: [], post_type: 'normal' });
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [artworkForm, setArtworkForm] = useState({
    title: '', artist: '', description: '', story: '', 
    image_url: '', images: [], year: new Date().getFullYear().toString(),
    medium: '', dimensions: '', is_original_for_sale: false, 
    original_price: null, allow_merchandise: false
  });
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingMainImage, setUploadingMainImage] = useState(false);
  const [uploadingSecondaryImage, setUploadingSecondaryImage] = useState(false);

  const isOwnProfile = user?.id === userId;

  useEffect(() => {
    fetchProfile();
    fetchUserPosts();
    fetchUserArtworks();
    fetchUserExhibitions();
  }, [userId]);

  useEffect(() => {
    if (isOwnProfile && activeTab === 'saved') {
      fetchSavedPosts();
    }
    if (isOwnProfile && activeTab === 'collected') {
      fetchSavedArtworks();
    }
    if (isOwnProfile && activeTab === 'customize') {
      fetchCustomizeOrders();
      fetchInvestments();
    }
  }, [activeTab, isOwnProfile]);

  const fetchProfile = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/users/${userId}/profile`, { headers });
      setProfile(res.data);
      setEditForm({
        name: res.data.name || '',
        bio: res.data.bio || '',
        avatar: res.data.avatar || '',
        user_title: res.data.user_title || '',
        user_type: res.data.user_type || 'artist'
      });
    } catch (err) {
      toast.error(t('profile.userNotFound', '用户不存在'));
      navigate('/community');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPosts = async () => {
    try {
      const res = await axios.get(`${API}/posts?user_id=${userId}`);
      setPosts(res.data);
    } catch (err) {
      console.error('Failed to fetch posts', err);
    }
  };

  const fetchUserArtworks = async () => {
    try {
      const res = await axios.get(`${API}/artworks?creator_id=${userId}`);
      setArtworks(res.data);
    } catch (err) {
      console.error('Failed to fetch artworks', err);
    }
  };

  const fetchSavedPosts = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/users/${userId}/saved-posts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedPosts(res.data);
    } catch (err) {
      console.error('Failed to fetch saved posts', err);
    }
  };

  const fetchSavedArtworks = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/users/${userId}/saved-artworks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedArtworks(res.data);
    } catch (err) {
      console.error('Failed to fetch saved artworks', err);
    }
  };

  const fetchCustomizeOrders = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/customize/orders/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomizeOrders(res.data);
    } catch (err) {
      console.error('Failed to fetch customize orders', err);
    }
  };

  const fetchInvestments = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/customize/investments/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvestments(res.data);
    } catch (err) {
      console.error('Failed to fetch investments', err);
    }
  };

  const fetchUserExhibitions = async () => {
    try {
      const res = await axios.get(`${API}/exhibitions?created_by=${userId}`);
      setExhibitions(res.data);
    } catch (err) {
      console.error('Failed to fetch exhibitions', err);
    }
  };

  const handleFollow = async () => {
    if (!user) {
      toast.error(t('common.loginRequired', '请先登录'));
      navigate('/login');
      return;
    }
    try {
      const res = await axios.post(`${API}/users/${userId}/follow`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile({
        ...profile,
        is_following: res.data.following,
        followers_count: profile.followers_count + (res.data.following ? 1 : -1)
      });
    } catch (err) {
      toast.error(t('common.error', '操作失败'));
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/users/me/profile`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile({ ...profile, ...res.data });
      setShowEditDialog(false);
      toast.success(t('profile.updateSuccess', '资料更新成功'));
    } catch (err) {
      toast.error(t('common.error', '保存失败'));
    } finally {
      setSaving(false);
    }
  };

  const handleUploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditForm({ ...editForm, avatar: res.data.url });
      toast.success(t('common.uploadSuccess', '上传成功'));
    } catch (err) {
      toast.error(t('common.uploadFailed', '上传失败'));
    } finally {
      setUploading(false);
    }
  };

  const handleCreatePost = async () => {
    if (!newPost.content.trim()) {
      toast.error(t('community.enterContent', '请输入内容'));
      return;
    }
    setSaving(true);
    try {
      if (editingPostId) {
        // 编辑模式
        await axios.put(`${API}/community/posts/${editingPostId}`, newPost, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('common.updateSuccess', '更新成功'));
        setEditingPostId(null);
      } else {
        // 创建模式
        await axios.post(`${API}/posts`, newPost, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('community.publishSuccess', '发布成功'));
        setProfile({ ...profile, posts_count: (profile.posts_count || 0) + 1 });
      }
      setShowCreatePost(false);
      setNewPost({ content: '', images: [], tags: [], post_type: 'normal' });
      fetchUserPosts();
    } catch (err) {
      toast.error(t('common.error', '操作失败'));
    } finally {
      setSaving(false);
    }
  };

  // 创建或编辑作品
  const handleCreateArtwork = async () => {
    if (!artworkForm.title.trim()) {
      toast.error(t('creator.enterTitle', '请输入作品标题'));
      return;
    }
    if (!artworkForm.image_url) {
      toast.error(t('creator.uploadImage', '请上传作品图片'));
      return;
    }
    setSaving(true);
    try {
      const artworkData = {
        ...artworkForm,
        artist: artworkForm.artist || user?.name || ''
      };
      
      if (editingArtworkId) {
        // 编辑模式
        await axios.put(`${API}/artworks/${editingArtworkId}`, artworkData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('common.updateSuccess', '更新成功'));
        setEditingArtworkId(null);
      } else {
        // 创建模式
        await axios.post(`${API}/creators/me/artworks`, artworkData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('creator.artworkCreated', '作品发布成功'));
      }
      
      setShowCreateArtwork(false);
      setArtworkForm({
        title: '', artist: '', description: '', story: '', 
        image_url: '', images: [], year: new Date().getFullYear().toString(),
        medium: '', dimensions: '', is_original_for_sale: false, 
        original_price: null, allow_merchandise: false
      });
      fetchUserArtworks();
    } catch (err) {
      toast.error(t('common.error', '操作失败：') + (err.response?.data?.detail || '未知错误'));
    } finally {
      setSaving(false);
    }
  };

  // 上传作品主图
  const handleUploadArtworkImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // 检查文件大小（10MB限制）
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('creator.imageTooLarge', '图片太大，最大支持10MB'));
      return;
    }
    
    // 检查文件类型
    if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
      toast.error(t('creator.invalidImageType', '不支持的图片格式，请上传JPG、PNG、GIF或WebP格式'));
      return;
    }
    
    setUploadingMainImage(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 60000 // 60秒超时
      });
      setArtworkForm({ ...artworkForm, image_url: res.data.url });
      toast.success(t('creator.imageUploaded', '图片上传成功'));
    } catch (err) {
      console.error('Upload error:', err);
      if (err.code === 'ECONNABORTED') {
        toast.error(t('creator.uploadTimeout', '上传超时，请检查网络后重试'));
      } else if (err.response?.status === 413) {
        toast.error(t('creator.imageTooLarge', '图片太大，最大支持10MB'));
      } else {
        toast.error(t('common.uploadFailed', '上传失败，请重试'));
      }
    } finally {
      setUploadingMainImage(false);
      // 清除input的值，允许重新选择同一文件
      e.target.value = '';
    }
  };

  // 上传作品副图
  const handleUploadArtworkSecondaryImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // 限制最多3张副图
    if (artworkForm.images.length >= 3) {
      toast.error(t('creator.maxImagesReached', '最多可上传3张副图'));
      return;
    }
    
    // 检查文件大小（10MB限制）
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('creator.imageTooLarge', '图片太大，最大支持10MB'));
      return;
    }
    
    // 检查文件类型
    if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
      toast.error(t('creator.invalidImageType', '不支持的图片格式，请上传JPG、PNG、GIF或WebP格式'));
      return;
    }
    
    setUploadingSecondaryImage(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 60000 // 60秒超时
      });
      setArtworkForm({ 
        ...artworkForm, 
        images: [...artworkForm.images, res.data.url] 
      });
      toast.success(t('creator.secondaryImageUploaded', '副图上传成功'));
    } catch (err) {
      console.error('Upload error:', err);
      if (err.code === 'ECONNABORTED') {
        toast.error(t('creator.uploadTimeout', '上传超时，请检查网络后重试'));
      } else if (err.response?.status === 413) {
        toast.error(t('creator.imageTooLarge', '图片太大，最大支持10MB'));
      } else {
        toast.error(t('common.uploadFailed', '上传失败，请重试'));
      }
    } finally {
      setUploadingSecondaryImage(false);
      // 清除input的值，允许重新选择同一文件
      e.target.value = '';
    }
  };

  // 删除作品副图
  const handleRemoveArtworkSecondaryImage = (index) => {
    const newImages = artworkForm.images.filter((_, i) => i !== index);
    setArtworkForm({ ...artworkForm, images: newImages });
  };

  const compressImage = (file, maxWidth = 1920, quality = 0.85) => {
    return new Promise((resolve) => {
      if (file.size <= 2 * 1024 * 1024) {
        resolve(file);
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            resolve(new File([blob], file.name, { type: 'image/jpeg' }));
          }, 'image/jpeg', quality);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleUploadPostImage = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    
    setUploading(true);
    const uploadedUrls = [...newPost.images];
    let failCount = 0;
    
    for (const rawFile of files) {
      if (uploadedUrls.length >= 9) break;
      try {
        const file = await compressImage(rawFile);
        const formData = new FormData();
        formData.append('file', file);
        
        const res = await axios.post(`${API}/upload/image`, formData, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000,
        });
        uploadedUrls.push(res.data.url);
        setNewPost(prev => ({ ...prev, images: [...uploadedUrls] }));
      } catch (err) {
        failCount++;
        const detail = err.response?.data?.detail || '';
        if (err.response?.status === 413 || detail.includes('large')) {
          toast.error(t('common.fileTooLarge', 'File too large. Please use a smaller image.'));
        } else if (err.response?.status === 503) {
          toast.error(t('common.storageUnavailable', 'Storage service temporarily unavailable.'));
        } else {
          toast.error(`${t('common.uploadFailed', 'Upload failed')}: ${rawFile.name}`);
        }
      }
    }
    
    setNewPost(prev => ({ ...prev, images: uploadedUrls }));
    if (failCount > 0 && files.length > 1) {
      toast.warning(`${files.length - failCount}/${files.length} ${t('common.photosUploaded', 'photos uploaded successfully')}`);
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleChangePassword = async () => {
    if (passwordForm.new !== passwordForm.confirm) {
      toast.error(t('profile.passwordMismatch', '两次输入的密码不一致'));
      return;
    }
    if (passwordForm.new.length < 6) {
      toast.error(t('profile.passwordTooShort', '新密码至少需要6位'));
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.current,
        new_password: passwordForm.new
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('profile.passwordChanged', '密码修改成功'));
      setShowPasswordDialog(false);
      setPasswordForm({ current: '', new: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error', '操作失败'));
    } finally {
      setSaving(false);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !newPost.tags.includes(tagInput.trim())) {
      setNewPost({ ...newPost, tags: [...newPost.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const handleLikeChange = (postId, isLiked, likesCount) => {
    setPosts(prev => prev.map(p => 
      p.id === postId ? { ...p, is_liked: isLiked, likes_count: likesCount } : p
    ));
    setSavedPosts(prev => prev.map(p => 
      p.id === postId ? { ...p, is_liked: isLiked, likes_count: likesCount } : p
    ));
  };

  // 编辑帖子
  const handleEditPost = (post) => {
    setNewPost({
      content: post.content || '',
      images: post.images || [],
      tags: post.tags || [],
      post_type: post.post_type || 'normal'
    });
    setEditingPostId(post.id);
    setShowCreatePost(true);
  };

  // 删除帖子
  const handleDeletePost = async (postId) => {
    if (!window.confirm(t('common.confirmDelete', '确定要删除吗？'))) return;
    
    try {
      await axios.delete(`${API}/community/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('common.deleteSuccess', '删除成功'));
      setPosts(prev => prev.filter(p => p.id !== postId));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error', '操作失败'));
    }
  };

  // 编辑作品
  const handleEditArtwork = (artwork) => {
    setArtworkForm({
      title: artwork.title || '',
      artist: artwork.artist || '',
      description: artwork.description || '',
      image_url: artwork.image_url || '',
      images: artwork.images || [],
      year: artwork.year || '',
      medium: artwork.medium || '',
      is_for_sale: artwork.is_for_sale || false,
      price: artwork.price || '',
      allow_merchandise: artwork.allow_merchandise || false
    });
    setEditingArtworkId(artwork.id);
    setShowCreateArtwork(true);
  };

  // 删除作品
  const handleDeleteArtwork = async (artworkId) => {
    if (!window.confirm(t('common.confirmDelete', '确定要删除吗？'))) return;
    
    try {
      await axios.delete(`${API}/artworks/${artworkId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('common.deleteSuccess', '删除成功'));
      setArtworks(prev => prev.filter(a => a.id !== artworkId));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error', '操作失败'));
    }
  };

  // 确认收货
  const handleConfirmReceipt = async (orderId) => {
    try {
      await axios.post(`${API}/customize/orders/${orderId}/confirm`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('customize.receiptConfirmed', '收货确认成功'));
      fetchCustomizeOrders();
      setShowOrderDetail(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error', '操作失败'));
    }
  };

  // 提交评价
  const handleSubmitReview = async () => {
    if (!selectedOrder) return;
    if (!reviewForm.rating) {
      toast.error(t('customize.ratingRequired', '请选择评分'));
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/customize/orders/${selectedOrder.id}/review`, reviewForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('customize.reviewSubmitted', '评价提交成功'));
      setShowReviewDialog(false);
      setReviewForm({ rating: 5, comment: '' });
      fetchCustomizeOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error', '提交失败'));
    } finally {
      setSaving(false);
    }
  };

  // 获取订单状态翻译
  const getOrderStatusText = (status) => {
    const statusMap = {
      pending: t('customize.statusPending', '待付款'),
      paid: t('customize.statusPaid', '已付款'),
      processing: t('customize.statusProcessing', '生产中'),
      shipped: t('customize.statusShipped', '已发货'),
      completed: t('customize.statusCompleted', '已完成'),
      cancelled: t('customize.statusCancelled', '已取消')
    };
    return statusMap[status] || status;
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
        </div>
      </>
    );
  }

  if (!profile) return null;

  const displayPosts = activeTab === 'posts' ? posts : activeTab === 'saved' ? savedPosts : [];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-amber-50/50 pt-16" data-testid="profile-page">
        <div className="max-w-4xl mx-auto">
          {/* 返回按钮 */}
          <div className="bg-amber-50 border-b px-4 py-3">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate(-1)}
              className="text-amber-700/80 hover:text-amber-900"
            >
              ← {t('common.back', '返回')}
            </Button>
          </div>
          
          {/* Instagram风格头部 */}
          <div className="bg-amber-50 border-b">
            <div className="p-6 md:p-8">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-6 md:gap-12">
                {/* 头像 */}
                <div className="w-24 h-24 md:w-36 md:h-36 rounded-full overflow-hidden bg-gradient-to-br from-green-500 via-emerald-500 to-teal-500 p-[3px] flex-shrink-0">
                  <div className="w-full h-full rounded-full bg-amber-50 p-[3px]">
                    <div className="w-full h-full rounded-full overflow-hidden bg-amber-200">
                      {profile.avatar ? (
                        <img loading="lazy"
                          src={getImageUrl(profile.avatar)} 
                          alt={profile.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-amber-100">
                          <User className="w-12 h-12 md:w-16 md:h-16 text-amber-500" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* 信息区 */}
                <div className="flex-1 text-center md:text-left">
                  {/* 用户名和按钮 */}
                  <div className="flex flex-col md:flex-row items-center gap-4 mb-4">
                    <h1 className="text-xl md:text-2xl font-light">{profile.name}</h1>
                    <div className="flex items-center gap-2">
                      {isOwnProfile ? (
                        <>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setShowEditDialog(true)}
                          >
                            {t('profile.editProfile', '编辑资料')}
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button 
                            variant={profile.is_following ? "outline" : "default"}
                            size="sm"
                            onClick={handleFollow}
                            className={!profile.is_following ? "bg-blue-500 hover:bg-blue-600" : ""}
                          >
                            {profile.is_following ? (
                              <><UserCheck className="w-4 h-4 mr-1" /> {t('community.following', '已关注')}</>
                            ) : (
                              <><UserPlus className="w-4 h-4 mr-1" /> {t('community.follow', '关注')}</>
                            )}
                          </Button>
                          {user && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => navigate(`/messages?to=${userId}`)}
                            >
                              <MessageCircle className="w-4 h-4" />
                            </Button>
                          )}
                        </>
                      )}
                      {/* 显示user_title，但过滤掉可能是邮箱地址的值（包含@的） */}
                      {profile.user_title && !profile.user_title.includes('@') && (
                        <Badge className="bg-purple-500 text-white">
                          {profile.user_title}
                        </Badge>
                      )}
                      {profile.user_type && profile.user_type !== 'user' && (
                        <Badge variant="outline" className="border-amber-500 text-amber-700 bg-amber-50">
                          {profile.user_type === 'artist' ? t('userTypes.artist', '艺术家') :
                           profile.user_type === 'collector' ? t('userTypes.collector', '收藏家') :
                           profile.user_type === 'museum' ? t('userTypes.museum', '美术馆') :
                           profile.user_type === 'curator' ? t('userTypes.curator', '策展人') : profile.user_type}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* 统计数据 */}
                  <div className="flex justify-center md:justify-start gap-8 mb-4">
                    <div className="text-center">
                      <span className="font-semibold">{posts.length}</span>
                      <span className="text-amber-600 ml-1">{t('profile.posts', '动态')}</span>
                    </div>
                    <div className="text-center">
                      <span className="font-semibold">{artworks.length}</span>
                      <span className="text-amber-600 ml-1">{t('profile.artworks', '作品')}</span>
                    </div>
                    <div className="text-center cursor-pointer hover:opacity-70">
                      <span className="font-semibold">{profile.followers_count || 0}</span>
                      <span className="text-amber-600 ml-1">{t('community.followers', '粉丝')}</span>
                    </div>
                    <div className="text-center cursor-pointer hover:opacity-70">
                      <span className="font-semibold">{profile.following_count || 0}</span>
                      <span className="text-amber-600 ml-1">{t('community.following', '关注')}</span>
                    </div>
                  </div>

                  {/* 简介 */}
                  {profile.bio && (
                    <p className="text-sm">{profile.bio}</p>
                  )}

                  {/* 发布动态和发布作品按钮（自己的主页） */}
                  {isOwnProfile && (
                    <div className="flex gap-2 mt-4">
                      <Button 
                        className="bg-amber-600 hover:bg-amber-700"
                        onClick={() => setShowCreatePost(true)}
                      >
                        <Plus className="w-4 h-4 mr-2" /> {t('profile.newPost', '发布动态')}
                      </Button>
                      <Button 
                        variant="outline"
                        className="border-amber-600 text-amber-600 hover:bg-amber-50"
                        onClick={() => setShowCreateArtwork(true)}
                      >
                        <Palette className="w-4 h-4 mr-2" /> {t('profile.newArtwork', '发布作品')}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 标签页 */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="w-full justify-center border-t bg-transparent h-auto p-0">
                <TabsTrigger 
                  value="posts" 
                  className="flex-1 max-w-[120px] gap-2 py-3 rounded-none border-t-2 border-transparent data-[state=active]:border-amber-600 data-[state=active]:bg-transparent"
                >
                  <Grid3X3 className="w-4 h-4" />
                  <span className="hidden md:inline">{t('profile.posts', '动态')}</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="artworks" 
                  className="flex-1 max-w-[120px] gap-2 py-3 rounded-none border-t-2 border-transparent data-[state=active]:border-amber-600 data-[state=active]:bg-transparent"
                >
                  <Palette className="w-4 h-4" />
                  <span className="hidden md:inline">{t('profile.artworks', '作品')}</span>
                </TabsTrigger>
                {exhibitions.length > 0 && (
                  <TabsTrigger 
                    value="exhibitions" 
                    className="flex-1 max-w-[120px] gap-2 py-3 rounded-none border-t-2 border-transparent data-[state=active]:border-amber-600 data-[state=active]:bg-transparent"
                  >
                    <Calendar className="w-4 h-4" />
                    <span className="hidden md:inline">{t('profile.exhibitions', '展览')}</span>
                  </TabsTrigger>
                )}
                {isOwnProfile && (
                  <TabsTrigger 
                    value="collected" 
                    className="flex-1 max-w-[120px] gap-2 py-3 rounded-none border-t-2 border-transparent data-[state=active]:border-amber-600 data-[state=active]:bg-transparent"
                  >
                    <Heart className="w-4 h-4" />
                    <span className="hidden md:inline">{t('profile.collectedArtworks', '收藏作品')}</span>
                  </TabsTrigger>
                )}
                {isOwnProfile && (
                  <TabsTrigger 
                    value="customize" 
                    className="flex-1 max-w-[120px] gap-2 py-3 rounded-none border-t-2 border-transparent data-[state=active]:border-amber-600 data-[state=active]:bg-transparent"
                  >
                    <ShoppingBag className="w-4 h-4" />
                    <span className="hidden md:inline">{t('profile.customize', '定制')}</span>
                  </TabsTrigger>
                )}
              </TabsList>
            </Tabs>
          </div>

          {/* 内容网格 - Instagram风格 */}
          <div className="bg-amber-50">
            {activeTab === 'exhibitions' ? (
              // 展览网格
              exhibitions.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4">
                  {exhibitions.map((exhibition) => (
                    <div
                      key={exhibition.id}
                      className="relative group cursor-pointer overflow-hidden rounded-xl bg-amber-100"
                      onClick={() => navigate(`/exhibitions/${exhibition.id}`)}
                    >
                      <div className="aspect-video">
                        {exhibition.cover_image ? (
                          <img loading="lazy"
                            src={getImageUrl(exhibition.cover_image)}
                            alt={exhibition.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                            <Calendar className="w-12 h-12 text-white/50" />
                          </div>
                        )}
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                      <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                        <h3 className="font-semibold line-clamp-1">{exhibition.title}</h3>
                        <p className="text-sm text-white/70">{exhibition.artwork_count || 0} {t('profile.artworkPieces', '件作品')}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-16 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full border-2 border-amber-300 flex items-center justify-center">
                    <Calendar className="w-8 h-8 text-amber-400" />
                  </div>
                  <p className="text-amber-600">{t('profile.noExhibitions', '暂无展览')}</p>
                </div>
              )
            ) : activeTab === 'collected' ? (
              // 收藏作品网格
              savedArtworks.length > 0 ? (
                <div className="grid grid-cols-3 gap-[2px] md:gap-1">
                  {savedArtworks.map((artwork) => (
                    <div
                      key={artwork.id}
                      className="aspect-square relative group cursor-pointer overflow-hidden bg-amber-100"
                      onClick={() => navigate(`/artwork/${artwork.id}`)}
                    >
                      <img loading="lazy"
                        src={getImageUrl(artwork.image_url)}
                        alt={artwork.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-amber-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center p-2">
                        <p className="text-white font-semibold text-center line-clamp-2">{artwork.title}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-16 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full border-2 border-amber-300 flex items-center justify-center">
                    <Heart className="w-8 h-8 text-amber-400" />
                  </div>
                  <p className="text-amber-600">{t('profile.noCollectedArtworks', '暂无收藏作品')}</p>
                </div>
              )
            ) : activeTab === 'customize' ? (
              // 定制订单和投资
              <div className="p-4 space-y-6">
                {/* 定制订单 */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <ShoppingBag className="w-5 h-5 text-orange-500" />
                    {t('profile.customizeOrders', '定制订单')}
                  </h3>
                  {customizeOrders.length > 0 ? (
                    <div className="space-y-3">
                      {customizeOrders.map((order) => (
                        <div 
                          key={order.id} 
                          className="bg-amber-50/50 rounded-xl p-4 flex gap-4 cursor-pointer hover:bg-amber-100 transition-colors"
                          onClick={() => { setSelectedOrder(order); setShowOrderDetail(true); }}
                          data-testid={`order-card-${order.id}`}
                        >
                          <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                            <img loading="lazy"
                              src={getImageUrl(order.artwork_image)}
                              alt={order.artwork_title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium line-clamp-1">{order.artwork_title}</h4>
                            <p className="text-sm text-amber-600">{order.product_name} × {order.quantity}</p>
                            {order.size && <p className="text-xs text-amber-500">{t('customize.size', '尺码')}: {order.size}</p>}
                            {/* 物流信息提示 */}
                            {order.status === 'shipped' && order.tracking_number && (
                              <p className="text-xs text-indigo-600 flex items-center gap-1 mt-1">
                                <Truck className="w-3 h-3" />
                                {order.tracking_company}: {order.tracking_number}
                              </p>
                            )}
                            <div className="flex items-center justify-between mt-2">
                              <span className="font-bold text-orange-500">{order.total_amount} {t('customize.currency', '度电')}</span>
                              <Badge className={
                                order.status === 'paid' ? 'bg-blue-500' :
                                order.status === 'processing' ? 'bg-purple-500' :
                                order.status === 'shipped' ? 'bg-indigo-500' :
                                order.status === 'completed' ? 'bg-amber-500' :
                                'bg-yellow-500'
                              }>
                                {getOrderStatusText(order.status)}
                              </Badge>
                            </div>
                            {/* 操作按钮 */}
                            <div className="flex gap-2 mt-2">
                              {order.status === 'shipped' && (
                                <Button 
                                  size="sm" 
                                  className="bg-amber-500 hover:bg-amber-600 h-7 text-xs"
                                  onClick={(e) => { e.stopPropagation(); handleConfirmReceipt(order.id); }}
                                  data-testid={`confirm-receipt-${order.id}`}
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  {t('customize.confirmReceipt', '确认收货')}
                                </Button>
                              )}
                              {order.status === 'completed' && !order.has_review && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="h-7 text-xs border-orange-500 text-orange-500 hover:bg-orange-50"
                                  onClick={(e) => { e.stopPropagation(); setSelectedOrder(order); setShowReviewDialog(true); }}
                                  data-testid={`review-order-${order.id}`}
                                >
                                  <Star className="w-3 h-3 mr-1" />
                                  {t('customize.writeReview', '写评价')}
                                </Button>
                              )}
                              {order.has_review && (
                                <span className="text-xs text-amber-500 flex items-center">
                                  <Star className="w-3 h-3 mr-1 text-yellow-500 fill-yellow-500" />
                                  {t('customize.reviewed', '已评价')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-amber-500">
                      <ShoppingBag className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>{t('customize.noOrders', '暂无定制订单')}</p>
                      <Button 
                        variant="outline" 
                        className="mt-4"
                        onClick={() => navigate('/artworks')}
                      >
                        {t('customize.browseArtworks', '浏览作品去定制')}
                      </Button>
                    </div>
                  )}
                </div>

                {/* 投资记录 */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-orange-500" />
                    {t('profile.investments', '投资记录')}
                  </h3>
                  {investments.length > 0 ? (
                    <div className="space-y-3">
                      {investments.map((inv) => (
                        <div key={inv.id} className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl p-4 flex gap-4">
                          <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                            <img loading="lazy"
                              src={getImageUrl(inv.artwork_image)}
                              alt={inv.artwork_title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium line-clamp-1">{inv.artwork_title}</h4>
                            <p className="text-sm text-amber-600">{t('customize.investAmount', '投资金额')}: {inv.amount} {t('customize.currency', '度电')}</p>
                            <p className="text-xs text-amber-500">{t('customize.revenueShare', '收益分成')}: {inv.revenue_share}%</p>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-sm">{t('customize.totalRevenue', '累计收益')}: <span className="font-bold text-amber-600">{inv.total_revenue || 0} {t('customize.currency', '度电')}</span></span>
                              <Badge className={
                                inv.status === 'active' ? 'bg-amber-500' :
                                inv.status === 'completed' ? 'bg-amber-50/500' :
                                'bg-yellow-500'
                              }>
                                {inv.status === 'active' ? t('customize.investActive', '生效中') :
                                 inv.status === 'completed' ? t('customize.investCompleted', '已完成') :
                                 t('customize.investPending', '待生效')}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-amber-500">
                      <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>{t('customize.noInvestments', '暂无投资记录')}</p>
                      <Button 
                        variant="outline" 
                        className="mt-4"
                        onClick={() => navigate('/artworks')}
                      >
                        {t('customize.learnInvest', '了解投资收益')}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ) : activeTab === 'artworks' ? (
              // 作品集网格
              artworks.length > 0 ? (
                <div className="grid grid-cols-3 gap-[2px] md:gap-1">
                  {artworks.map((artwork) => (
                    <div
                      key={artwork.id}
                      className="aspect-square relative group cursor-pointer overflow-hidden bg-amber-100"
                      onClick={() => navigate(`/artwork/${artwork.id}`)}
                    >
                      <img loading="lazy"
                        src={getImageUrl(artwork.image_url)}
                        alt={artwork.title}
                        className="w-full h-full object-cover"
                      />
                      {/* 悬停遮罩 */}
                      <div className="absolute inset-0 bg-amber-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center p-2">
                        <p className="text-white font-semibold text-center line-clamp-2">{artwork.title}</p>
                        {artwork.artist && (
                          <p className="text-white/80 text-sm mt-1">{artwork.artist}</p>
                        )}
                        {/* 编辑/删除按钮 - 仅对自己的作品显示 */}
                        {isOwnProfile && (
                          <div className="flex gap-2 mt-3">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-7 text-xs bg-amber-50/20 border-white/50 text-white hover:bg-amber-50/30"
                              onClick={(e) => { e.stopPropagation(); handleEditArtwork(artwork); }}
                            >
                              <Edit3 className="w-3 h-3 mr-1" />
                              {t('common.edit', '编辑')}
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-7 text-xs bg-red-500/80 border-red-500 text-white hover:bg-red-600"
                              onClick={(e) => { e.stopPropagation(); handleDeleteArtwork(artwork.id); }}
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              {t('common.delete', '删除')}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-16 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full border-2 border-amber-300 flex items-center justify-center">
                    <Palette className="w-8 h-8 text-amber-400" />
                  </div>
                  <p className="text-amber-600">
                    {isOwnProfile ? t('profile.noArtworks', '你还没有发布任何作品') : t('profile.noArtworksOther', '暂无作品')}
                  </p>
                </div>
              )
            ) : displayPosts.length > 0 ? (
              // 帖子/收藏网格
              <div className="grid grid-cols-3 gap-[2px] md:gap-1">
                {displayPosts.map((post) => (
                  <div
                    key={post.id}
                    className="aspect-square relative group cursor-pointer overflow-hidden bg-amber-100"
                    onClick={() => setSelectedPost(post)}
                  >
                    {post.images?.[0] ? (
                      <img loading="lazy"
                        src={getImageUrl(post.images[0])}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center p-4">
                        <p className="text-sm text-amber-600 text-center line-clamp-4">{post.content}</p>
                      </div>
                    )}
                    {/* 悬停遮罩 */}
                    <div className="absolute inset-0 bg-amber-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                      <div className="flex items-center gap-6">
                        <div className="flex items-center gap-1 text-white font-semibold">
                          <Heart className="w-5 h-5 fill-white" />
                          {post.likes_count || 0}
                        </div>
                        <div className="flex items-center gap-1 text-white font-semibold">
                          <MessageCircle className="w-5 h-5 fill-white" />
                          {post.comments_count || 0}
                        </div>
                      </div>
                      {/* 编辑/删除按钮 - 仅对自己的帖子显示 */}
                      {isOwnProfile && activeTab === 'posts' && (
                        <div className="flex gap-2 mt-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-7 text-xs bg-amber-50/20 border-white/50 text-white hover:bg-amber-50/30"
                            onClick={(e) => { e.stopPropagation(); handleEditPost(post); }}
                          >
                            <Edit3 className="w-3 h-3 mr-1" />
                            {t('common.edit', '编辑')}
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-7 text-xs bg-red-500/80 border-red-500 text-white hover:bg-red-600"
                            onClick={(e) => { e.stopPropagation(); handleDeletePost(post.id); }}
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            {t('common.delete', '删除')}
                          </Button>
                        </div>
                      )}
                    </div>
                    {/* 多图标识 */}
                    {post.images?.length > 1 && (
                      <div className="absolute top-2 right-2">
                        <svg className="w-5 h-5 text-white drop-shadow" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z"/>
                        </svg>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-16 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full border-2 border-amber-300 flex items-center justify-center">
                  {activeTab === 'posts' ? (
                    <Grid3X3 className="w-8 h-8 text-amber-400" />
                  ) : (
                    <Bookmark className="w-8 h-8 text-amber-400" />
                  )}
                </div>
                <p className="text-amber-600">
                  {activeTab === 'posts' 
                    ? (isOwnProfile ? t('profile.noPosts', '你还没有发布任何动态') : t('profile.noPostsOther', '暂无动态'))
                    : t('profile.noSaved', '暂无收藏')
                  }
                </p>
                {isOwnProfile && activeTab === 'posts' && (
                  <Button 
                    className="mt-4 bg-amber-600 hover:bg-amber-700"
                    onClick={() => setShowCreatePost(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" /> {t('profile.createFirstPost', '发布第一条动态')}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* 帖子详情弹窗（复用ArtworkModal的样式逻辑） */}
        {selectedPost && (
          <PostModal
            post={selectedPost}
            isOpen={!!selectedPost}
            onClose={() => setSelectedPost(null)}
            onLikeChange={handleLikeChange}
          />
        )}

        {/* 编辑资料对话框 */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('profile.editProfile', '编辑资料')}</DialogTitle>
              <DialogDescription className="sr-only">修改您的个人资料信息</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* 头像 */}
              <div className="flex flex-col items-center gap-4">
                <div className="w-24 h-24 rounded-full overflow-hidden bg-amber-200">
                  {editForm.avatar ? (
                    <img src={getImageUrl(editForm.avatar)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="w-10 h-10 text-amber-500" />
                    </div>
                  )}
                </div>
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" onChange={handleUploadAvatar} className="hidden" />
                  <span className="text-blue-500 text-sm hover:underline">
                    {uploading ? t('common.uploading', '上传中...') : t('profile.changeAvatar', '更换头像')}
                  </span>
                </label>
              </div>
              
              {/* 昵称 */}
              <div className="space-y-2">
                <Label>{t('profile.nickname', '昵称')}</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder={t('profile.nicknamePlaceholder', '输入昵称')}
                />
              </div>
              
              {/* 身份标签 - 多选 */}
              <div className="space-y-2">
                <Label>{t('profile.userTitle', '我是')}</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {['画家', '收藏家', '旅行家', '梦想家', '美食家', '摄影家', '设计师', '创作者'].map(title => {
                    const titles = editForm.user_title ? editForm.user_title.split('·').map(t => t.trim()) : [];
                    const isSelected = titles.includes(title);
                    return (
                      <Badge 
                        key={title}
                        variant={isSelected ? 'default' : 'outline'}
                        className={`cursor-pointer transition-all ${isSelected ? 'bg-purple-500' : 'hover:bg-purple-100'}`}
                        onClick={() => {
                          if (isSelected) {
                            // 取消选择
                            const newTitles = titles.filter(t => t !== title);
                            setEditForm({ ...editForm, user_title: newTitles.join(' · ') });
                          } else {
                            // 添加选择
                            const newTitles = [...titles.filter(t => t), title];
                            setEditForm({ ...editForm, user_title: newTitles.join(' · ') });
                          }
                        }}
                      >
                        {title}
                      </Badge>
                    );
                  })}
                </div>
                <Input
                  value={editForm.user_title}
                  onChange={(e) => setEditForm({ ...editForm, user_title: e.target.value })}
                  placeholder={t('profile.userTitlePlaceholder', '可多选或自定义，用"·"分隔')}
                />
              </div>

              {/* 用户类型 - 决定在哪个分类页面显示 */}
              <div className="space-y-2">
                <Label>{t('userTypes.selectLabel')} <span className="text-amber-500 text-xs">({t('userTypes.selectHint')})</span></Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'artist', label: t('userTypes.artist'), desc: t('userTypes.artistShortDesc') },
                    { value: 'collector', label: t('userTypes.collector'), desc: t('userTypes.collectorShortDesc') },
                    { value: 'museum', label: t('userTypes.museum'), desc: t('userTypes.museumShortDesc') },
                    { value: 'curator', label: t('userTypes.curator'), desc: t('userTypes.curatorShortDesc') }
                  ].map(type => (
                    <div
                      key={type.value}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        editForm.user_type === type.value 
                          ? 'border-amber-500 bg-amber-50' 
                          : 'border-amber-200 hover:border-amber-300'
                      }`}
                      onClick={() => setEditForm({ ...editForm, user_type: type.value })}
                    >
                      <div className="font-medium text-sm">{type.label}</div>
                      <div className="text-xs text-amber-600">{type.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* 个人简介 */}
              <div className="space-y-2">
                <Label>{t('profile.bio', '个人简介')}</Label>
                <Textarea
                  value={editForm.bio}
                  onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                  placeholder={t('profile.bioPlaceholder', '介绍一下自己吧...')}
                  rows={3}
                />
              </div>
              
              {/* 修改密码区域 */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Lock className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-medium text-amber-700">{t('profile.changePassword', '修改密码')}</span>
                </div>
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-amber-600">{t('profile.currentPassword', '当前密码')}</Label>
                    <div className="relative">
                      <Input
                        type={showCurrentPw ? "text" : "password"}
                        value={passwordForm.current}
                        onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
                        placeholder="••••••••"
                        className="pr-10"
                      />
                      <button 
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-amber-500 hover:text-amber-700/80"
                        onClick={() => setShowCurrentPw(!showCurrentPw)}
                      >
                        {showCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-amber-600">{t('profile.newPassword', '新密码')}</Label>
                    <div className="relative">
                      <Input
                        type={showNewPw ? "text" : "password"}
                        value={passwordForm.new}
                        onChange={(e) => setPasswordForm({ ...passwordForm, new: e.target.value })}
                        placeholder="••••••••"
                        className="pr-10"
                      />
                      <button 
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-amber-500 hover:text-amber-700/80"
                        onClick={() => setShowNewPw(!showNewPw)}
                      >
                        {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-amber-600">{t('profile.confirmPassword', '确认新密码')}</Label>
                    <div className="relative">
                      <Input
                        type={showNewPw ? "text" : "password"}
                        value={passwordForm.confirm}
                        onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                        placeholder="••••••••"
                        className="pr-10"
                      />
                    </div>
                  </div>
                  {passwordForm.current && (
                    <Button 
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={handleChangePassword} 
                      disabled={saving || !passwordForm.current || !passwordForm.new}
                      className="w-full"
                    >
                      {saving ? t('common.saving', '保存中...') : t('profile.updatePassword', '更新密码')}
                    </Button>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                {t('common.cancel', '取消')}
              </Button>
              <Button onClick={handleSaveProfile} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
                {saving ? t('common.saving', '保存中...') : t('common.save', '保存')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 删除单独的修改密码对话框，已整合到编辑资料中 */}

        {/* 发布动态对话框 */}
        <Dialog open={showCreatePost} onOpenChange={(open) => {
          setShowCreatePost(open);
          if (!open) {
            setEditingPostId(null);
            setNewPost({ content: '', images: [], tags: [], post_type: 'normal' });
          }
        }}>
          <DialogContent className="max-w-lg max-h-[90vh]" style={{display:'flex', flexDirection:'column'}}>
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>{editingPostId ? t('common.edit', '编辑') + t('profile.post', '动态') : t('profile.newPost', '发布动态')}</DialogTitle>
              <DialogDescription>{t('profile.shareLife', '分享你的素食生活')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4 overflow-y-auto flex-1 min-h-0">
              <Textarea
                value={newPost.content}
                onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                placeholder={t('profile.sharePlaceholder', '分享你的想法...')}
                rows={3}
              />
              <div className="space-y-2">
                <Label>{t('profile.addImages', '添加图片')} ({newPost.images.length}/9)</Label>
                <div className="flex flex-wrap gap-2">
                  {newPost.images.map((img, idx) => (
                    <div key={idx} className="relative">
                      <img src={getImageUrl(img)} alt="" className="w-16 h-16 object-cover rounded" />
                      <button
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center"
                        onClick={() => setNewPost({ ...newPost, images: newPost.images.filter((_, i) => i !== idx) })}
                      >×</button>
                    </div>
                  ))}
                  {newPost.images.length < 9 && (
                    <label className="w-16 h-16 border-2 border-dashed rounded flex items-center justify-center cursor-pointer hover:bg-amber-50/50">
                      <input type="file" accept="image/*" multiple onChange={handleUploadPostImage} className="hidden" />
                      {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5 text-amber-500" />}
                    </label>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t('profile.addTags', '添加标签')}</Label>
                <div className="flex gap-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    placeholder={t('profile.tagPlaceholder', '输入标签')}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  />
                  <Button variant="outline" onClick={addTag}>{t('common.add', '添加')}</Button>
                </div>
                {newPost.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {newPost.tags.map((tag) => (
                      <Badge 
                        key={tag} 
                        variant="secondary" 
                        className="cursor-pointer" 
                        onClick={() => setNewPost({ ...newPost, tags: newPost.tags.filter(t => t !== tag) })}
                      >
                        #{tag} ×
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter className="flex-shrink-0 border-t pt-4">
              <Button variant="outline" onClick={() => setShowCreatePost(false)}>
                {t('common.cancel', '取消')}
              </Button>
              <Button onClick={handleCreatePost} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
                {saving ? t('community.publishing', '发布中...') : t('community.post', '发布')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 发布作品对话框 */}
        <Dialog open={showCreateArtwork} onOpenChange={(open) => {
          setShowCreateArtwork(open);
          if (!open) {
            setEditingArtworkId(null);
            setArtworkForm({
              title: '', artist: '', description: '', story: '', 
              image_url: '', images: [], year: new Date().getFullYear().toString(),
              medium: '', dimensions: '', is_original_for_sale: false, 
              original_price: null, allow_merchandise: false
            });
          }
        }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingArtworkId ? t('common.edit', '编辑') + t('profile.artwork', '作品') : t('profile.newArtwork', '发布作品')}</DialogTitle>
              <DialogDescription>{t('creator.uploadDescription', '上传您的艺术作品，与世界分享您的创意')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* 作品标题 */}
              <div className="space-y-2">
                <Label>{t('creator.title', '作品标题')} *</Label>
                <Input 
                  value={artworkForm.title}
                  onChange={(e) => setArtworkForm({ ...artworkForm, title: e.target.value })}
                  placeholder={t('creator.titlePlaceholder', '输入作品名称')}
                />
              </div>
              
              {/* 作者名称 */}
              <div className="space-y-2">
                <Label>{t('creator.artist', '作者')}</Label>
                <Input 
                  value={artworkForm.artist}
                  onChange={(e) => setArtworkForm({ ...artworkForm, artist: e.target.value })}
                  placeholder={t('creator.artistPlaceholder', '输入作者姓名（留空则使用您的用户名）')}
                />
                <p className="text-xs text-amber-600">{t('creator.artistHint', '如果留空，将显示您的用户名')}</p>
              </div>
              
              {/* 作品主图 */}
              <div className="space-y-2">
                <Label>{t('creator.mainImage', '作品主图')} *</Label>
                {artworkForm.image_url ? (
                  <div className="relative inline-block">
                    <img loading="lazy"
                      src={artworkForm.image_url.startsWith('http') ? artworkForm.image_url : `${process.env.REACT_APP_BACKEND_URL || ''}${artworkForm.image_url}`}
                      alt="作品预览"
                      className="max-w-full max-h-48 rounded border"
                    />
                    <button
                      type="button"
                      onClick={() => setArtworkForm({ ...artworkForm, image_url: '' })}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <label className="flex-1 border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:bg-amber-50/50">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleUploadArtworkImage}
                        className="hidden"
                        disabled={uploadingMainImage}
                      />
                      {uploadingMainImage ? (
                        <Loader2 className="w-8 h-8 mx-auto animate-spin text-amber-500" />
                      ) : (
                        <>
                          <Plus className="w-8 h-8 mx-auto text-amber-500" />
                          <p className="text-sm text-amber-600 mt-2">{t('creator.clickToUpload', '点击上传图片')}</p>
                        </>
                      )}
                    </label>
                  </div>
                )}
              </div>

              {/* 作品副图 */}
              <div className="space-y-2">
                <Label>{t('creator.secondaryImages', '作品副图')} <span className="text-amber-500 text-sm">({t('creator.optional', '可选')}，{t('creator.maxThreeImages', '最多3张')})</span></Label>
                <div className="flex flex-wrap gap-3">
                  {/* 已上传的副图 */}
                  {artworkForm.images.map((img, index) => (
                    <div key={index} className="relative">
                      <img loading="lazy"
                        src={img.startsWith('http') ? img : `${process.env.REACT_APP_BACKEND_URL || ''}${img}`}
                        alt={`副图 ${index + 1}`}
                        className="w-24 h-24 object-cover rounded border"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveArtworkSecondaryImage(index)}
                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 text-xs"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {/* 上传按钮（如果副图少于3张） */}
                  {artworkForm.images.length < 3 && (
                    <label className="w-24 h-24 border-2 border-dashed rounded flex flex-col items-center justify-center cursor-pointer hover:bg-amber-50/50">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleUploadArtworkSecondaryImage}
                        className="hidden"
                        disabled={uploadingSecondaryImage}
                      />
                      {uploadingSecondaryImage ? (
                        <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                      ) : (
                        <>
                          <Plus className="w-6 h-6 text-amber-500" />
                          <span className="text-xs text-amber-500 mt-1">{t('creator.addImage', '添加')}</span>
                        </>
                      )}
                    </label>
                  )}
                </div>
                <p className="text-xs text-amber-500">{t('creator.secondaryImagesHint', '可上传作品的不同角度、细节或创作过程')}</p>
              </div>
              
              {/* 作品描述 */}
              <div className="space-y-2">
                <Label>{t('creator.description', '作品描述')}</Label>
                <Textarea 
                  value={artworkForm.description}
                  onChange={(e) => setArtworkForm({ ...artworkForm, description: e.target.value })}
                  placeholder={t('creator.descriptionPlaceholder', '描述您的作品...')}
                  rows={3}
                />
              </div>
              
              {/* 创作年份和媒介 */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('creator.year', '创作年份')}</Label>
                  <Input 
                    value={artworkForm.year}
                    onChange={(e) => setArtworkForm({ ...artworkForm, year: e.target.value })}
                    placeholder="2024"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('creator.medium', '创作媒介')}</Label>
                  <Input 
                    value={artworkForm.medium}
                    onChange={(e) => setArtworkForm({ ...artworkForm, medium: e.target.value })}
                    placeholder={t('creator.mediumPlaceholder', '油画、水彩...')}
                  />
                </div>
              </div>
              
              {/* 销售设置 */}
              <div className="space-y-3 pt-2 border-t">
                <h4 className="font-medium text-sm text-amber-700">{t('creator.saleSettings', '销售设置')}</h4>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{t('creator.originalForSale', '原作出售')}</p>
                    <p className="text-xs text-amber-600">{t('creator.originalForSaleDesc', '允许出售原作（平台只收居间服务费1.5%）')}</p>
                  </div>
                  <Switch 
                    checked={artworkForm.is_original_for_sale}
                    onCheckedChange={(v) => setArtworkForm({ ...artworkForm, is_original_for_sale: v })}
                  />
                </div>
                {artworkForm.is_original_for_sale && (
                  <div className="space-y-2">
                    <Label>{t('creator.originalPrice', '原作价格 (¥)')}</Label>
                    <Input 
                      type="number"
                      value={artworkForm.original_price || ''}
                      onChange={(e) => setArtworkForm({ ...artworkForm, original_price: parseFloat(e.target.value) || null })}
                      placeholder="10000"
                    />
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{t('creator.allowMerchandise', '授权周边')}</p>
                    <p className="text-xs text-amber-600">{t('creator.allowMerchandiseDesc', '允许制作周边产品（您获得10%分成）')}</p>
                  </div>
                  <Switch 
                    checked={artworkForm.allow_merchandise}
                    onCheckedChange={(v) => setArtworkForm({ ...artworkForm, allow_merchandise: v })}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateArtwork(false)}>{t('common.cancel', '取消')}</Button>
              <Button onClick={handleCreateArtwork} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {t('creator.publish', '发布作品')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 订单详情弹窗 */}
        <Dialog open={showOrderDetail} onOpenChange={setShowOrderDetail}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t('customize.orderDetail', '订单详情')}</DialogTitle>
            </DialogHeader>
            {selectedOrder && (
              <div className="space-y-4">
                {/* 作品信息 */}
                <div className="flex items-center gap-4 p-4 bg-amber-50/50 rounded-lg">
                  <img loading="lazy"
                    src={getImageUrl(selectedOrder.artwork_image)}
                    alt=""
                    className="w-20 h-20 rounded-lg object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold truncate">{selectedOrder.artwork_title}</p>
                    <p className="text-sm text-amber-600">{selectedOrder.product_name}</p>
                    {selectedOrder.size && <p className="text-xs text-amber-500">{t('customize.size', '尺码')}: {selectedOrder.size}</p>}
                    <p className="text-sm">{t('customize.quantity', '数量')}: {selectedOrder.quantity}</p>
                  </div>
                </div>
                
                {/* 状态和金额 */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-amber-600">{t('customize.orderStatus', '订单状态')}</p>
                    <Badge className={
                      selectedOrder.status === 'paid' ? 'bg-blue-500' :
                      selectedOrder.status === 'processing' ? 'bg-purple-500' :
                      selectedOrder.status === 'shipped' ? 'bg-indigo-500' :
                      selectedOrder.status === 'completed' ? 'bg-amber-500' :
                      'bg-yellow-500'
                    }>
                      {getOrderStatusText(selectedOrder.status)}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-amber-600">{t('customize.totalAmount', '订单金额')}</p>
                    <p className="font-bold text-orange-500">{selectedOrder.total_amount} {t('customize.currency', '度电')}</p>
                  </div>
                </div>

                {/* 物流信息 */}
                {selectedOrder.tracking_number && (
                  <div className="p-4 bg-indigo-50 rounded-lg">
                    <p className="text-sm font-medium text-indigo-600 flex items-center gap-2">
                      <Truck className="w-4 h-4" />
                      {t('customize.shippingInfo', '物流信息')}
                    </p>
                    <p className="text-sm mt-1">{t('customize.carrier', '快递公司')}: {selectedOrder.tracking_company}</p>
                    <p className="text-sm">{t('customize.trackingNumber', '快递单号')}: {selectedOrder.tracking_number}</p>
                  </div>
                )}

                {/* 下单时间 */}
                <div>
                  <p className="text-sm text-amber-600">{t('customize.orderTime', '下单时间')}</p>
                  <p className="text-sm">{new Date(selectedOrder.created_at).toLocaleString()}</p>
                </div>

                {/* 操作按钮 */}
                <div className="flex gap-2 pt-2 border-t">
                  {selectedOrder.status === 'shipped' && (
                    <Button 
                      className="flex-1 bg-amber-500 hover:bg-amber-600"
                      onClick={() => handleConfirmReceipt(selectedOrder.id)}
                      data-testid="confirm-receipt-dialog-btn"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      {t('customize.confirmReceipt', '确认收货')}
                    </Button>
                  )}
                  {selectedOrder.status === 'completed' && !selectedOrder.has_review && (
                    <Button 
                      className="flex-1 bg-orange-500 hover:bg-orange-600"
                      onClick={() => { setShowOrderDetail(false); setShowReviewDialog(true); }}
                      data-testid="write-review-dialog-btn"
                    >
                      <Star className="w-4 h-4 mr-2" />
                      {t('customize.writeReview', '写评价')}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* 评价弹窗 */}
        <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t('customize.reviewTitle', '评价订单')}</DialogTitle>
            </DialogHeader>
            {selectedOrder && (
              <div className="space-y-4">
                {/* 作品信息 */}
                <div className="flex items-center gap-3 p-3 bg-amber-50/50 rounded-lg">
                  <img loading="lazy"
                    src={getImageUrl(selectedOrder.artwork_image)}
                    alt=""
                    className="w-14 h-14 rounded-lg object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{selectedOrder.artwork_title}</p>
                    <p className="text-sm text-amber-600">{selectedOrder.product_name}</p>
                  </div>
                </div>

                {/* 评分 */}
                <div>
                  <Label className="text-sm">{t('customize.rating', '评分')}</Label>
                  <div className="flex gap-2 mt-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                        className="focus:outline-none transition-transform hover:scale-110"
                        data-testid={`rating-star-${star}`}
                      >
                        <Star 
                          className={`w-8 h-8 ${
                            star <= reviewForm.rating 
                              ? 'text-yellow-400 fill-yellow-400' 
                              : 'text-amber-400'
                          }`} 
                        />
                      </button>
                    ))}
                  </div>
                </div>

                {/* 评价内容 */}
                <div>
                  <Label className="text-sm">{t('customize.reviewComment', '评价内容')}</Label>
                  <Textarea
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                    placeholder={t('customize.reviewPlaceholder', '分享您的使用体验...')}
                    rows={3}
                    className="mt-2"
                    data-testid="review-comment-input"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReviewDialog(false)}>
                {t('common.cancel', '取消')}
              </Button>
              <Button 
                onClick={handleSubmitReview} 
                disabled={saving}
                className="bg-orange-500 hover:bg-orange-600"
                data-testid="submit-review-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {t('customize.submitReview', '提交评价')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

// 帖子详情弹窗组件
const PostModal = ({ post, isOpen, onClose, onLikeChange }) => {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isLiked, setIsLiked] = useState(post?.is_liked || false);
  const [likesCount, setLikesCount] = useState(post?.likes_count || 0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const allImages = post?.images || [];

  useEffect(() => {
    if (isOpen && post) {
      fetchComments();
      setIsLiked(post.is_liked || false);
      setLikesCount(post.likes_count || 0);
    }
  }, [isOpen, post]);

  const fetchComments = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/posts/${post.id}/comments`);
      setComments(res.data);
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    if (!user) {
      toast.error(t('common.loginRequired', '请先登录'));
      return;
    }
    try {
      const res = await axios.post(`${API}/posts/${post.id}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsLiked(res.data.liked);
      setLikesCount(res.data.likes_count);
      onLikeChange?.(post.id, res.data.liked, res.data.likes_count);
    } catch (err) {
      toast.error(t('common.error', '操作失败'));
    }
  };

  const handleComment = async () => {
    if (!user) {
      toast.error(t('common.loginRequired', '请先登录'));
      return;
    }
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/posts/${post.id}/comments`, { content: newComment.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments([res.data, ...comments]);
      setNewComment('');
    } catch (err) {
      toast.error(t('common.error', '评论失败'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!post) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl p-0 overflow-hidden bg-amber-50 max-h-[90vh]">
        <div className="flex flex-col md:flex-row h-full">
          {/* 左侧：图片/内容区域 */}
          <div className="relative bg-black flex-1 min-h-[300px] md:min-h-[500px] flex items-center justify-center">
            {allImages.length > 0 ? (
              <>
                <img loading="lazy"
                  src={getImageUrl(allImages[currentImageIndex])}
                  alt=""
                  className="max-w-full max-h-[90vh] object-contain"
                />
                {allImages.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length)}
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-amber-50/80 rounded-full flex items-center justify-center hover:bg-amber-50"
                    >
                      ‹
                    </button>
                    <button
                      onClick={() => setCurrentImageIndex((prev) => (prev + 1) % allImages.length)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-amber-50/80 rounded-full flex items-center justify-center hover:bg-amber-50"
                    >
                      ›
                    </button>
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1">
                      {allImages.map((_, idx) => (
                        <div key={idx} className={`w-2 h-2 rounded-full ${idx === currentImageIndex ? 'bg-amber-50' : 'bg-amber-50/50'}`} />
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="text-white p-8 text-center">
                <p className="text-lg">{post.content}</p>
              </div>
            )}
          </div>

          {/* 右侧：信息和互动区域 */}
          <div className="w-full md:w-[350px] flex flex-col border-l max-h-[90vh]">
            {/* 作者信息 */}
            <div className="p-4 border-b flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-full overflow-hidden bg-amber-200 cursor-pointer"
                onClick={() => { onClose(); navigate(`/profile/${post.user_id}`); }}
              >
                {post.user_avatar ? (
                  <img src={getImageUrl(post.user_avatar)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="w-5 h-5 text-amber-500" />
                  </div>
                )}
              </div>
              <span className="font-semibold text-sm cursor-pointer hover:underline" onClick={() => { onClose(); navigate(`/profile/${post.user_id}`); }}>
                {post.user_name}
              </span>
            </div>

            {/* 内容和评论 */}
            <div className="flex-1 overflow-y-auto p-4">
              {allImages.length > 0 && post.content && (
                <p className="text-sm mb-4">{post.content}</p>
              )}
              
              {post.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-4">
                  {post.tags.map(tag => (
                    <Badge key={tag} variant="outline" className="text-xs">#{tag}</Badge>
                  ))}
                </div>
              )}

              {/* 评论列表 */}
              <div className="space-y-4">
                {loading ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                  </div>
                ) : comments.length > 0 ? (
                  comments.map((comment) => (
                    <div key={comment.id} className="flex gap-3">
                      <div className="w-8 h-8 rounded-full overflow-hidden bg-amber-200 flex-shrink-0">
                        {comment.user_avatar ? (
                          <img src={getImageUrl(comment.user_avatar)} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <User className="w-4 h-4 text-amber-500" />
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="text-sm">
                          <span className="font-semibold mr-2">{comment.user_name}</span>
                          {comment.content}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-amber-500 text-sm py-4">
                    {t('community.noComments', '暂无评论')}
                  </p>
                )}
              </div>
            </div>

            {/* 底部互动 */}
            <div className="border-t">
              <div className="p-3 flex items-center gap-4">
                <button onClick={handleLike} className="hover:opacity-70">
                  <Heart className={`w-6 h-6 ${isLiked ? 'fill-red-500 text-red-500' : ''}`} />
                </button>
                <button className="hover:opacity-70">
                  <MessageCircle className="w-6 h-6" />
                </button>
              </div>
              <div className="px-3 pb-2">
                <p className="font-semibold text-sm">{likesCount} {t('community.likes', '人喜欢')}</p>
              </div>
              <div className="p-3 border-t flex items-center gap-2">
                <Input
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder={t('community.addComment', '添加评论...')}
                  className="flex-1 border-none shadow-none focus-visible:ring-0"
                  onKeyDown={(e) => e.key === 'Enter' && handleComment()}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleComment}
                  disabled={!newComment.trim() || submitting}
                  className="text-blue-500"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.send', '发送')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserProfilePage;
